import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, apiError, logError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      logError("auth session check", e);
      if (e?.response?.status === 428) {
        setUser({ must_change_password: true, email: e?.response?.data?.email || "" });
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const completeGoogleLogin = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId });
      setUser(data);
      return { ok: true };
    } catch (e) {
      setUser(false);
      return { ok: false, error: apiError(e) };
    } finally {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, mustChangePassword: Boolean(data.must_change_password) };
    } catch (e) {
      return { ok: false, error: apiError(e), status: e.response?.status };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logError("logout", e);
    }
    setUser(false);
  }, []);

  const can = useCallback(
    (module, action = "view") =>
      Boolean(user && user.permissions && (user.permissions[module] || []).includes(action)),
    [user]
  );

  const value = useMemo(
    () => ({ user, loading, login, logout, can, reload: load, loginWithGoogle,
             completeGoogleLogin }),
    [user, loading, login, logout, can, load, loginWithGoogle, completeGoogleLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
